import React, { useEffect, useState, useRef } from 'react';
import { injectIntl } from 'react-intl';
import Pagination from '@uniiverse/library/components/pagination';
import { Link } from 'react-router-dom';
import MediaQuery from 'react-responsive';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { ErrorFlash } from 'components/create/error-flash/ErrorFlash';
import { Search as SearchInput } from 'components/my-events/search';
import { Dropdown } from 'semantic-ui-react';
import { Input } from '../../inputs';
import Button from '../../button';
import AddOns from '../../../assets/add-ons/addOns.svg';
import DesktopAddOnsTable from './desktop-table';
import MobileAddOnsTable from './mobile-table';
import StatusFilters from '../status-filter';
import { locale } from './locale';
import DeleteModal from '../delete-modal/DeleteAddonModal';
import getAddOnsQuery from '../get-add-ons.gql';
import UPDATE_ADDON_MUTATION from '../update-add-on.gql';
import DELETE_ADDON_MUTATION from '../delete-add-on.gql';
import EmptyStateIcon from '../../../assets/add-ons/discount-code.svg';
import WhitePlusSign from '../../../assets/add-ons/plus_white.svg';
import FilterSign from '../../../assets/add-ons/filter.svg';
import Search from '../../../assets/add-ons/search.svg';
import SearchIcon from '../../../assets/search.svg';
import Close from '../../../assets/add-ons/close_blue.svg';
import styles from './styles-module.css';
import mobileStyles from './mobile-styles-module.css';

import {
  ACTIVE,
  INACTIVE,
  SHOW_ALL,
  SMALL_DEVICE_BREAK_POINT,
  CREATE_ADDON_URL,
  CREATE_EVENT_URL,
  ADD_ONS_LIMIT,
  ADD_ON_LISTING_STATES,
  HOSTED_LISTING_LIMIT
} from '../consts';

const AddOnsTable = injectIntl((props) => {
  const { intl, hostId, selectedStatus, handleStateOnFilterChange, hasPermissionForManageAddOns } = props;
  const [allAddOnsCount, setAllAddOnsCount] = useState(0);
  const [serverErrors, setServerErrors] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [listings, setListings] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [addOnsData, setAddOnsData] = useState({});
  const [updateAddOnMutation] = useMutation(UPDATE_ADDON_MUTATION);
  const [deleteAddOnMutation] = useMutation(DELETE_ADDON_MUTATION);
  const [pageCount, setPageCount] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteIdx, setDeleteIdx] = useState(-1);
  const [addOnSearch, setAddOnSearch] = useState(null);
  const [searchedEventList, setSearchedEventList] = useState(listings);
  const [searchEventKey, setSearchEventKey] = useState('');
  const [selectedListing, setSelectedListing] = useState(null);
  const [allListings, setAllListings] = useState(null);
  const isEventSelected = useRef(false);
  const isFirstFetch = useRef(true);
  const [isEventSelectorFocused, setIsEventSelectorFocused] = useState(false);
  const isHostChanged = useRef(false);

  const getAddOnsQueryVariables = (
    addOnSearchKey,
    addOnStatus = selectedStatus,
    addOnListingId = selectedListing?.listingId,
    addOnsListingCurrency = selectedListing?.currency,
    addOnListOffset = 0,
  ) => {
    return {
      addOnSearch: addOnSearchKey,
      hostId,
      limit: ADD_ONS_LIMIT,
      offset: addOnListOffset,
      state: addOnStatus,
      eventStates: ADD_ON_LISTING_STATES,
      eventLimit: HOSTED_LISTING_LIMIT,
      listingId: addOnListingId,
      currency: addOnsListingCurrency,
    }
  }

  const getFilteredAddOnSearchKey = () =>  addOnSearch?.length > 0 ? addOnSearch : null;

  const { data, loading, fetchMore } = useQuery(getAddOnsQuery, {
    variables: getAddOnsQueryVariables(getFilteredAddOnSearchKey()),
    skip: !isFirstFetch,
  });

  useEffect(() => {
    removeAllFilters();
    isHostChanged.current = true;
    isFirstFetch.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.hostId])

  useEffect(()=> {
    if (!loading && data && isFirstFetch.current) {
      const { totalCount } = data.host.addOns;
      setAddOnsData(data);
      if(selectedStatus === SHOW_ALL){
        setAllAddOnsCount(totalCount);
      }
      setAddOns(data.host.addOns.nodes);
      setListings(data.host?.events?.nodes);
      if (allListings === null || allListings.length === 0 || isHostChanged.current) {
        setAllListings(data.host?.events?.nodes);
        isHostChanged.current = false;
      }
      setPageCount(Math.ceil(totalCount / ADD_ONS_LIMIT));
    }
  }, [loading, data, selectedStatus, allListings]);

  const renderErrorMessages = (errors) => {
    setServerErrors({ obj: { deleteSoldAddOn: errors[0] }});
  }

  const handleOnAddOnsDelete = (index) => {
    setShowDeleteModal(true);
    setDeleteIdx(index);
  }

  const handleOnCloseDeleteModalClicked = () => {
    setShowDeleteModal(false);
    setDeleteIdx(-1);
  }

  const handleDeleteAddOn = async () => {
    if (deleteIdx === -1) {
      return;
    }

    deleteAddOnMutation({ variables: { id: addOns[deleteIdx].id, hostId } }).then((response) => {
      const { errors } = response.data.deleteAddOn;
      if (errors?.length > 0) {
        renderErrorMessages(errors);
      }
      else {
        getUpdatedAddOnsList(true);
      }
    }).catch((err) => {
      renderErrorMessages(err);
    }).finally(() => {
      setShowDeleteModal(false);
    });
  }

  const handleQueryOnFilterChange = (status) => {
    isFirstFetch.current = false;
    fetchMore({
      variables:
        getAddOnsQueryVariables(
          getFilteredAddOnSearchKey(),
          status,
          selectedListing?.listingId,
          selectedListing?.currency,
          0
        ),
      updateQuery: (prev, { fetchMoreResult }) => {
        if (fetchMoreResult) {
          setAddOnsData(fetchMoreResult);
          const { totalCount, nodes } = fetchMoreResult.host.addOns;
          (status === SHOW_ALL) && setAllAddOnsCount(totalCount)
          setAddOns(nodes);
          setListings(fetchMoreResult.host?.events?.nodes);
          setPageCount(Math.ceil(totalCount / ADD_ONS_LIMIT));
        }
        else {
          setAddOnsData(prev);
        }
      },
    });
    setCurrentPage(1);
  }

  const handleChangePage = ({ selected }) => {
    isFirstFetch.current = false;
    fetchMore({
      variables: 
        getAddOnsQueryVariables(
          getFilteredAddOnSearchKey(),
          selectedStatus,
          selectedListing?.listingId,
          selectedListing?.currency,
          ADD_ONS_LIMIT * selected
        ),
      updateQuery: (_, { fetchMoreResult }) => addOnResponse(fetchMoreResult, selected + 1)     
    });
    window.scrollTo(0, 0);
  }

  const getSelectedPage = (isDeleteOperation = false) => {
    let selectedPage = currentPage;
    const hasOneAddOnInPage = addOns?.length % ADD_ONS_LIMIT === 1;
    const shouldMoveToPreviousPage =  hasOneAddOnInPage && currentPage !== 1;
    if (shouldMoveToPreviousPage) {
      if (selectedStatus !== SHOW_ALL || (selectedStatus === SHOW_ALL && isDeleteOperation)) {
        selectedPage = currentPage - 1;
      }
    }
    return selectedPage;
  }

  const getUpdatedAddOnsList = (isDeleteOperation = false) => {
    isFirstFetch.current = false;
    const currentSelectedPage = getSelectedPage(isDeleteOperation);
    fetchMore({
      variables: getAddOnsQueryVariables(
        getFilteredAddOnSearchKey(),
        selectedStatus,
        selectedListing?.listingId,
        selectedListing?.currency,
        ADD_ONS_LIMIT * (currentSelectedPage - 1)
      ),
      updateQuery: (_, { fetchMoreResult }) => addOnResponse(fetchMoreResult, currentSelectedPage)
    });
  }

  const updateAddOn = async (input) => {
    const { id, taxIds, attributes } = input;
    try {
      const response = await updateAddOnMutation({ variables: { id, taxIds, hostId, addon: attributes }});
      const {
        data: {
          updateAddOn: {
            errors,
          }
        }
      } = response;

      if (errors) {
        renderErrorMessages(errors);
      }
      else {
        getUpdatedAddOnsList();
      }
    }
    catch(err) {
      renderErrorMessages(err);
    }
  }

  const handleOnAddOnsStatusChange = (index, checked) => {
    isFirstFetch.current = false;
    const newState = checked ? ACTIVE : INACTIVE;
       
    const newRates = addOns[index].rates.map(r => {
      return Object.assign({}, r, { state: newState });
    });
     
    const newAddon = Object.assign({}, addOns[index], {
      rates: newRates
    });
       
    setAddOns([...addOns.slice(0, index), newAddon, ...addOns.slice(index + 1)]);

    const addOnRateInputs = addOns[index].rates.map(r => {
      return ({ id: r.id, attributes: { state: newState }});
    });

    const addOnInput = {
      id: addOns[index].id,
      taxIds: addOns[index].taxes.map((tax) => tax.id),
      hostId,
      attributes: {
        rates: addOnRateInputs
      }
    };

    updateAddOn(addOnInput);
  }

  const emptyEvents = Object.keys(addOnsData).length &&
    (addOnsData.error || (!loading && addOnsData.host.events.totalCount === 0));
  const emptyAddOns = Object.keys(addOnsData).length &&
    (addOnsData.error || (!loading && allAddOnsCount === 0));
  const currentAddOnsCount = (Object.keys(addOnsData).length && !loading)
    ? addOnsData.host.addOns.totalCount
    : 0;
  const isFilterNotApplied = selectedStatus === null
    && addOnSearch === null
    && selectedListing === null;

  if (emptyEvents) {
    return (
      <div className={`${styles.addOnsTable} ${styles.emptyAddOnsTable}`}>
        <EmptyStateIcon className={styles.emptyStateIcon} />
        <div className={styles.emptyStateText}>{intl.formatMessage(locale.emptyEvents)}</div>
        <a href={CREATE_EVENT_URL}>
          <Button className={`blue ${styles.createAddOnBtn}`}>
            <WhitePlusSign />
            {intl.formatMessage(locale.createEvent)}
          </Button>
        </a>
      </div>
    );
  }
   else if (emptyAddOns && isFilterNotApplied) {
    return (
      <div className={`${styles.addOnsTable} ${styles.emptyAddOnsTable}`}>
        <EmptyStateIcon className={styles.emptyStateIcon} />
        <div className={styles.emptyStateText}>{intl.formatMessage(locale.emptyAddOns)}</div>
        <Link to={CREATE_ADDON_URL}>
          <Button className={`blue ${styles.createAddOnBtn}`}>
            <WhitePlusSign />
            {intl.formatMessage(locale.createAddOn)}
          </Button>
        </Link>
      </div>
    );
  }

  const setSelectedListingArgument = (listing) => {
    setSelectedListing(listing);
    setSearchEventKey(listing ? listing.title : '');
  }

  const handleRemoveAllFilters = () => {
    isFirstFetch.current = false;
    fetchMore({
      variables: getAddOnsQueryVariables(null, SHOW_ALL, null, null, 0),
      updateQuery: (_, { fetchMoreResult }) => {
        removeAllFilters();
        const { nodes, totalCount } = fetchMoreResult.host?.addOns;
        setAddOns(nodes);
        setListings(fetchMoreResult.host?.events?.nodes);
        setPageCount(Math.ceil(totalCount / ADD_ONS_LIMIT));
        setCurrentPage(1);
      },
    });
  }

  const removeAllFilters = () => {
    setAddOnSearch(null);
    setSelectedListingArgument();
    handleStateOnFilterChange(SHOW_ALL);
    isEventSelected.current = false;
  }

  const renderNoAddOnFound = (isFilterApplied = false) => {
    if (isFilterApplied) {
      return (
        <div className={styles.noAddOnsContainer}>
          <div><SearchIcon /></div>
          <div className={styles.noAddOnsMessage}>
            {props.intl.formatMessage(locale.noAddOnsFoundWithFilter)}
          </div>
          <div 
            className={`${styles.removeFilterButton} Button default-type default`} 
            onClick={handleRemoveAllFilters} 
            role="button" 
            tabIndex={0}
          >
            {props.intl.formatMessage(locale.removeAllFilters)}
          </div>
        </div>
      );
    }

    return (
      <div className={styles.noAddOnsContainer}>
        <div><AddOns /></div>
        <div className={styles.noAddOnsMessage}>
          {props.intl.formatMessage(locale.noAddOnsFound)}
        </div>
        <Link 
          to="/dashboard/add-ons/create" 
          target="_blank" 
          rel="noopener noreferrer" 
          className={`${styles.creatAddOnButton} Button default-type default`}
        >  
          {props.intl.formatMessage(locale.createAddOn)}
        </Link>
      </div> 
    );     
  }

  const handleSearch = (searchText) => {
    const filteredText = searchText?.length > 0 ? searchText : null;
    setAddOnSearch(filteredText);
    isFirstFetch.current = false;
    fetchMore({
      variables: getAddOnsQueryVariables(
        filteredText,
        selectedStatus,
        selectedListing?.listingId,
        selectedListing?.currency,
        0
      ),
      updateQuery: (_, { fetchMoreResult }) => addOnResponse(fetchMoreResult)
    });
  }

  const addOnResponse = (fetchMoreResult, selectedPage = 1) => {
    if (fetchMoreResult) {
      const { totalCount, nodes } = fetchMoreResult?.host?.addOns;
      const eventList = fetchMoreResult.host?.events?.nodes;
      setAddOns(nodes);
      setSearchedEventList(eventList);
      setPageCount(Math.ceil(totalCount / ADD_ONS_LIMIT));
      setCurrentPage(selectedPage);
    }
  }

  const renderAddOnStatusFilter = (isDesktop) => <StatusFilters
      selectedStatus={selectedStatus}
      stateOnFilterChange={handleStateOnFilterChange}
      queryOnFilterChange={handleQueryOnFilterChange}
      customClass={isDesktop ? styles.addonsListFilter : mobileStyles.addonsListFilter}
    />;

  const renderAddOnsSearch = (isDesktop) => {
    return <div className={isDesktop ? styles.addonsSearch : mobileStyles.addonsSearch}>
        <SearchInput
          onUpdateSearchText={(value) => handleSearch(value)}
          placeholder={intl.formatMessage(locale.addOnSearchPlaceholder)}
          intl={intl}
          customIcon={<Search className={styles.searchIcon} color="black" />}
          showLoadingState
          searchValue={addOnSearch || ''}
        />
      </div>
  }

  const handleEventSearch = (eventSearch) => {
    const filteredText = eventSearch?.length > 0 ? eventSearch : '';
    isEventSelected.current = false;
    setSearchEventKey(filteredText);
  }

  const getFilteredList = () => {
    const regexp = new RegExp(searchEventKey, 'i');
    return (allListings && regexp) ? [...allListings]?.filter(listing => regexp.test(listing.title)) : [];
  }

  const handleSuggestedEventSearch = (listingId, currency, title) => {
    setSelectedListingArgument({listingId, currency, title});
    setIsEventSelectorFocused(false);
    isEventSelected.current = true;
    isFirstFetch.current = false;
    fetchMore({
      variables: getAddOnsQueryVariables(addOnSearch, selectedStatus,listingId, currency, 0),
      updateQuery: (_, { fetchMoreResult }) => addOnResponse(fetchMoreResult)
    });
  }

  const removeEventFilter = () => {
    setSelectedListingArgument();
    isEventSelected.current = false;
    isFirstFetch.current = false;
    fetchMore({
      variables: getAddOnsQueryVariables(getFilteredAddOnSearchKey(), selectedStatus,null, null, 0),
      updateQuery: (_, { fetchMoreResult }) => addOnResponse(fetchMoreResult)
    });
  }

  const renderDropdownItemText = (text, searchKeyLength) => {
    if (searchKeyLength) {
      const regex = new RegExp(searchEventKey, 'gi');
      const parts = text.split(regex);
      const matches = text.match(regex);
      return (
        <div>
          {parts.map((part, index) => (
            <React.Fragment>
              {part}
              {matches && index < matches.length && <strong>{matches[index]}</strong>}
            </React.Fragment>
          ))}
        </div>
      );
    }
    return text;
  }

  const renderCancelIcon = <span
      className={styles.removeFilterIcon}
      onClick={removeEventFilter}
      role="button"
      tabIndex={0}
    >
      <Close color="black" />
    </span>;

  const handleSpaceDownOnEventInput = (event) => {
    const SPACE_BAR_CODE = 32;
    if (event.keyCode === SPACE_BAR_CODE) {
      event.target.value += ' '; 
      event.stopPropagation()
    }
  }

  const renderAddonsFilterByEvent = () => {
    const filterEventClass = 
      `${styles.addonsFilterByEvent} ${isEventSelected.current ? styles.addonsFilterByEventSelected : ''}`;

    return <div className={filterEventClass}>
      {isEventSelected.current ? renderCancelIcon : ''}
      <Dropdown
        trigger={
          <Input
            value={searchEventKey}
            onChange={(e) => {
              handleEventSearch(e.target.value);
            }}
            placeholder={intl.formatMessage(locale.eventSearchPlaceholder)}
            icon={() => <FilterSign className={styles.filterIcon} color="black" />}
            onKeyDown = {handleSpaceDownOnEventInput}
          />
        }
        className={styles.selector}
        searchQuery={searchEventKey}
        onFocus={_ => setIsEventSelectorFocused(true)}
        onBlur={_ => setIsEventSelectorFocused(false)}
      >
        <Dropdown.Menu className={styles.EventDropdownMenu} open={isEventSelectorFocused}>
          {getFilteredList()?.map(({ id, title, currency }) => {
            return <Dropdown.Item
              key={id}
              role="button"
              tabIndex={0}
              text={() => renderDropdownItemText(title, searchEventKey?.length)}
              onClick={() => handleSuggestedEventSearch(id, currency, title)}
            />
          })}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  }

  return (
    <div>
      <div className={styles.errorMessage}>
        <ErrorFlash errors={serverErrors} detailed />
      </div>
      <div className={styles.addOnsTable}>
        <MediaQuery minWidth={SMALL_DEVICE_BREAK_POINT + 1}>
          <DesktopAddOnsTable
            addOns={addOns}
            handleChangePage={handleChangePage}
            handleOnAddOnsStatusChange={handleOnAddOnsStatusChange}
            handleStateOnFilterChange={handleStateOnFilterChange}
            handleOnAddOnsDelete={handleOnAddOnsDelete}
            handleQueryOnFilterChange={handleQueryOnFilterChange}
            hostId={hostId}
            intl={intl}
            loading={loading}
            selectedStatus={selectedStatus}
            currentAddOnsCount={currentAddOnsCount}
            hasPermissionForManageAddOns={hasPermissionForManageAddOns}
            renderNoAddOnFound={renderNoAddOnFound}
            setAddOns={setAddOns}
            fetchMore={fetchMore}
            listings={listings}
            history={props.history}
            renderAddOnStatusFilter={renderAddOnStatusFilter}
            renderAddOnsSearch={renderAddOnsSearch}
            addOnSearch={addOnSearch}
            setAddOnSearch={setAddOnSearch}
            searchedEventList={searchedEventList}
            setSearchedEventList={setSearchedEventList}
            searchEventKey={searchEventKey} 
            setSearchEventKey={setSearchEventKey}
            renderAddonsFilterByEvent={renderAddonsFilterByEvent}
          />
        </MediaQuery>
        <MediaQuery maxWidth={SMALL_DEVICE_BREAK_POINT}>
          <MobileAddOnsTable
            addOns={addOns}
            handleChangePage={handleChangePage}
            handleOnAddOnsStatusChange={handleOnAddOnsStatusChange}
            handleStateOnFilterChange={handleStateOnFilterChange}
            handleQueryOnFilterChange={handleQueryOnFilterChange}
            handleOnAddOnsDelete={handleOnAddOnsDelete}
            hostId={hostId}
            intl={intl}
            loading={loading}
            selectedStatus={selectedStatus}
            currentAddOnsCount={currentAddOnsCount}
            hasPermissionForManageAddOns={hasPermissionForManageAddOns}
            renderNoAddOnFound={renderNoAddOnFound}
            fetchMore={fetchMore}
            history={props.history}
            setAddOns={setAddOns}
            listings={listings}
            renderAddOnStatusFilter={renderAddOnStatusFilter}
            renderAddOnsSearch={renderAddOnsSearch}
            addOnSearch={addOnSearch}
            setAddOnSearch={setAddOnSearch}
            searchedEventList={searchedEventList}
            setSearchedEventList={setSearchedEventList}
            searchEventKey={searchEventKey} 
            setSearchEventKey={setSearchEventKey}
            renderAddonsFilterByEvent={renderAddonsFilterByEvent}
          />
        </MediaQuery>

        {(!emptyAddOns && pageCount > 1) &&
          <div className={styles.footer}>
            <Pagination
              pageCount={pageCount}
              currentPage={currentPage}
              pageRangeDisplayed={4}
              marginPagesDisplayed={1}
              onPageChange={handleChangePage}
              hasPermissionForManageAddOns={hasPermissionForManageAddOns}
            />
          </div>
        }
        {
          showDeleteModal && <DeleteModal
            intl={intl}
            isOpen={showDeleteModal}
            onCloseClicked={handleOnCloseDeleteModalClicked}
            onDeleteClicked={handleDeleteAddOn}
            areYouSureContent={intl.formatMessage(locale.areYouSure)}
          />
        }
      </div>
    </div>
  )
});

export default AddOnsTable;
